import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Calendar, MapPin, Users, Clock, Tag } from "lucide-react";

export default function CampDetails() {
  const { slug } = useParams();
  const [camp, setCamp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    api
      .get(`/camps/${slug}`)
      .then(({ data }) => setCamp(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleRegister = () => {
    if (!user) {
      navigate("/signup", { state: { redirectTo: `/camps/${slug}/register` } });
    } else {
      navigate(`/camps/${slug}/register`);
    }
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="max-w-7xl mx-auto p-10 label-overline">Loading…</div>
      </PublicLayout>
    );
  }

  if (notFound || !camp) {
    return (
      <PublicLayout>
        <div className="max-w-7xl mx-auto p-10">
          <div className="font-display text-3xl uppercase">Camp not found</div>
          <Link to="/camps" className="text-[#E63946] underline text-sm mt-3 inline-block">
            ← Back to all camps
          </Link>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative bg-[#0A192F] text-white">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: `url(${camp.cover_image_url || ""})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F] via-[#0A192F]/80 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <Link to="/camps" className="label-overline text-white/60 hover:text-white">
            ← All Camps
          </Link>
          <div className="flex flex-wrap gap-2 mt-6">
            <span className="bg-[#E63946] text-white text-[10px] font-semibold uppercase tracking-wider px-3 py-1">
              {camp.type}
            </span>
            <span className="border border-white/30 text-white text-[10px] font-semibold uppercase tracking-wider px-3 py-1">
              {camp.status}
            </span>
          </div>
          <h1
            data-testid="camp-detail-title"
            className="font-display text-5xl lg:text-6xl uppercase tracking-tighter mt-4 max-w-4xl"
          >
            {camp.title}
          </h1>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to={`/camps/${camp.slug}/results`}
              data-testid="view-results-link"
              className="inline-flex items-center gap-2 border border-white/30 hover:bg-white hover:text-[#0A192F] px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm"
            >
              View Verified Results →
            </Link>
            <Link
              to="/media-wall"
              data-testid="camp-media-wall-link"
              className="inline-flex items-center gap-2 border border-white/30 hover:bg-white hover:text-[#0A192F] px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm"
            >
              CBR Media Wall →
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap gap-8 text-sm">
            <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#E63946]" />
              {new Date(camp.start_date).toLocaleDateString("en-CA", { dateStyle: "long" })} – {new Date(camp.end_date).toLocaleDateString("en-CA", { dateStyle: "long" })}
            </div>
            <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#E63946]" />{camp.venue}, {camp.city}, {camp.province}</div>
            <div className="flex items-center gap-2"><Users className="w-4 h-4 text-[#E63946]" />{camp.capacity} spots · {camp.registrations_count} registered</div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-10">
            <div>
              <div className="label-overline text-[#E63946]">About the Event</div>
              <h2 className="font-display text-2xl uppercase tracking-tight mt-2">Event Overview</h2>
              <p className="text-base leading-relaxed text-slate-700 mt-4 whitespace-pre-line">{camp.description}</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="border border-slate-200 p-5">
                <div className="label-overline">Type</div>
                <div className="font-display text-lg uppercase mt-1">{camp.type}</div>
              </div>
              <div className="border border-slate-200 p-5">
                <div className="label-overline">Capacity</div>
                <div className="font-data text-3xl mt-1">{camp.capacity}</div>
              </div>
              <div className="border border-slate-200 p-5">
                <div className="label-overline">Grad Years</div>
                <div className="font-data text-lg mt-1">{(camp.age_groups || []).join(" · ") || "All"}</div>
              </div>
              <div className="border border-slate-200 p-5">
                <div className="label-overline">Venue</div>
                <div className="text-sm font-medium mt-1">{camp.venue}</div>
                <div className="text-xs text-slate-500">{camp.city}, {camp.province}</div>
              </div>
            </div>

            <div className="border-l-4 border-[#E63946] bg-slate-50 p-5">
              <div className="label-overline text-[#E63946]">What&apos;s Included</div>
              <ul className="mt-3 space-y-2 text-sm text-slate-700">
                <li>· Full battery of verified metric testing (Exit Velo, 60-yard, Pop Time, Bullpen velocity)</li>
                <li>· Scouting evaluation by CBR national staff on 20-80 scale</li>
                <li>· Permanent CBR Prospect profile with all camp results</li>
                <li>· Player development report delivered via parent dashboard</li>
              </ul>
            </div>
          </div>

          {/* Registration card */}
          <aside className="lg:col-span-1">
            <div className="sticky top-32 bg-white border border-slate-200 rounded-sm p-6">
              <div className="label-overline">Registration</div>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="font-data text-5xl text-[#0A192F]">${camp.price}</span>
                <span className="text-slate-500 text-sm">{camp.currency}</span>
              </div>
              <div className="mt-1 text-xs text-slate-500">Includes all testing, evaluation & profile entry</div>

              <div className="mt-6 space-y-3 text-sm">
                <div className="flex items-center gap-2 text-slate-700"><Clock className="w-4 h-4" />
                  Deadline:{" "}
                  {camp.registration_deadline
                    ? new Date(camp.registration_deadline).toLocaleDateString("en-CA", { dateStyle: "medium" })
                    : "Open"}
                </div>
                <div className="flex items-center gap-2 text-slate-700"><Tag className="w-4 h-4" />
                  {camp.capacity - camp.registrations_count} spots remaining
                </div>
              </div>

              <button
                data-testid="camp-register-button"
                onClick={handleRegister}
                disabled={camp.status !== "open"}
                className="mt-6 w-full bg-[#E63946] hover:bg-[#D90429] disabled:bg-slate-300 disabled:cursor-not-allowed text-white py-4 text-sm font-semibold uppercase tracking-wider rounded-sm transition-colors"
              >
                {camp.status === "open" ? "Register Player" : "Registration Closed"}
              </button>
              <p className="text-[11px] text-slate-500 mt-3">
                You&apos;ll create a parent account, register your player, complete a waiver and pay through Stripe. Card
                data is never stored by CBR Prospects.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </PublicLayout>
  );
}
